import { useMemo } from 'react';
import {
  Box,
  ColorSwatch,
  Flex,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import {
  type FuelCategoryDisplay,
  FUEL_CATEGORY_MESSAGE_IDS,
  formatFuelCategoriesForDisplay,
} from '@/features/wildfireSlice';
import { type FuelCategories } from '@/generated/types/navigator/types.gen';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { FuelHazardStackedChart } from './FuelHazardStackedChart';

interface FuelsSectionProps {
  fuelCategories: FuelCategories;
  /**
   * Minimum percentage threshold to display a category. Defaults to 0 so every
   * non-zero category is shown; sub-1% values render as "Less than 1%".
   * @default 0
   */
  minPercentageThreshold?: number;
  /**
   * Test ID prefix for category and swatch elements
   * @default 'fuels-section'
   */
  'data-testid'?: string;
}

/**
 * Content component displaying fuel category information including:
 * - Stacked bar chart showing fuel composition
 * - List of fuel categories with color indicators and percentages
 */
export const FuelsSection = ({
  fuelCategories,
  minPercentageThreshold = 0,
  'data-testid': testId = 'fuels-section',
}: FuelsSectionProps) => {
  const theme = useMantineTheme();
  const intl = useClientTerminology();

  const fuelCategoriesForDisplay = useMemo<FuelCategoryDisplay[]>(() => {
    return formatFuelCategoriesForDisplay(
      fuelCategories,
      minPercentageThreshold
    );
  }, [fuelCategories, minPercentageThreshold]);

  const fuelColors: Record<keyof FuelCategories, string> = {
    grass: theme.colors.yellowBrand[6],
    shrub: theme.colors.orangeBrand[7],
    timber_litter: theme.colors.greenBrand[8],
    slash_blowdown: theme.colors.yellowBrand[9],
    non_burnable: theme.colors.grayBrand[5],
  };

  return (
    <Stack gap={0}>
      <Box pt="xs" pb="md">
        <FuelHazardStackedChart
          fuelCategories={fuelCategoriesForDisplay}
          minPercentageThreshold={minPercentageThreshold}
        />
      </Box>
      {fuelCategoriesForDisplay.map((fuelCategory) => {
        const displayName = intl.formatMessage({
          id: FUEL_CATEGORY_MESSAGE_IDS[fuelCategory.category],
        });
        const percentageLabel =
          fuelCategory.value * 100 < 1
            ? intl.formatMessage(
                { id: 'fuelDistribution.percentLessThan' },
                { value: 1 }
              )
            : `${Math.round(fuelCategory.value * 100)}%`;

        return (
          <Flex
            key={fuelCategory.category}
            justify="space-between"
            align="center"
            gap="xs"
            py="xs"
            data-testid={`${testId}-category-${fuelCategory.category}`}
          >
            <Flex gap="xs" align="center">
              <ColorSwatch
                withShadow={false}
                color={fuelColors[fuelCategory.category]}
                radius={theme.radius.sm}
                size={theme.spacing.md}
                data-testid={`${testId}-swatch-${fuelCategory.category}`}
              />
              <Text size={theme.other.typography.size.sm}>{displayName}</Text>
            </Flex>
            <Text size="sm" fw="medium">
              {percentageLabel}
            </Text>
          </Flex>
        );
      })}
    </Stack>
  );
};
